import type { InfoClient } from "@nktkas/hyperliquid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { queryKeys } from "#/hooks/query-keys";
import { getInfoClient, getSubscriptionClient } from "../lib/hyperliquid";
import { useTradeMarket } from "./use-trade-market";

type OrderBookSnapshot = NonNullable<Awaited<ReturnType<InfoClient["l2Book"]>>>;

export type OrderBookGrouping = {
	label: string;
	nSigFigs: 2 | 3 | 4 | 5;
	mantissa?: 2 | 5;
};

type DisplayedBook = {
	snapshot: OrderBookSnapshot;
	coin: string;
	grouping: OrderBookGrouping;
};

const DEFAULT_ORDERBOOK_GROUPING: OrderBookGrouping = {
	label: "default",
	nSigFigs: 5,
	mantissa: undefined,
};

function payloadMatchesStep(
	snapshot: OrderBookSnapshot | null,
	step: number,
): boolean {
	if (!snapshot) return false;
	if (!Number.isFinite(step) || step <= 0) return true;
	const sample = [
		...snapshot.levels[0].slice(0, 4),
		...snapshot.levels[1].slice(0, 4),
	];
	if (sample.length === 0) return true;
	for (const level of sample) {
		const price = Number(level.px);
		if (!Number.isFinite(price) || price <= 0) return false;
		const ratio = price / step;
		if (Math.abs(ratio - Math.round(ratio)) > 1e-3) return false;
	}
	return true;
}

function getOrderbookStepForGrouping(grouping: OrderBookGrouping): number {
	const parsed = Number(grouping.label.replaceAll(",", ""));
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function useTradeOrderBook(
	grouping: OrderBookGrouping = DEFAULT_ORDERBOOK_GROUPING,
) {
	const { market } = useTradeMarket();
	const infoClient = getInfoClient();
	const apiCoin = market.chartSymbol;
	const { nSigFigs, mantissa } = grouping;
	const expectedStep = getOrderbookStepForGrouping(grouping);

	const [displayed, setDisplayed] = useState<DisplayedBook | null>(null);

	const requestedRef = useRef({ coin: market.coin, grouping });
	requestedRef.current = { coin: market.coin, grouping };

	const snapshotQuery = useQuery({
		queryKey: queryKeys.trade.orderbook(apiCoin, nSigFigs, mantissa),
		queryFn: async () => {
			const snapshot = await infoClient.l2Book({
				coin: apiCoin,
				nSigFigs,
				mantissa,
			});
			return snapshot as OrderBookSnapshot | null;
		},
		staleTime: 300_000,
		refetchInterval: false,
		placeholderData: keepPreviousData,
	});

	const snapshotData = snapshotQuery.data;
	const isSnapshotFresh =
		!snapshotQuery.isPlaceholderData && !snapshotQuery.isFetching;
	useEffect(() => {
		if (!snapshotData || !isSnapshotFresh) return;
		const r = requestedRef.current;
		if (
			r.coin !== market.coin ||
			r.grouping.nSigFigs !== nSigFigs ||
			r.grouping.mantissa !== mantissa
		) {
			return;
		}
		if (!payloadMatchesStep(snapshotData, expectedStep)) return;
		setDisplayed({
			snapshot: snapshotData,
			coin: market.coin,
			grouping: r.grouping,
		});
	}, [
		snapshotData,
		isSnapshotFresh,
		market.coin,
		nSigFigs,
		mantissa,
		expectedStep,
	]);

	useEffect(() => {
		const client = getSubscriptionClient();
		if (!client) return;

		let cancelled = false;
		let unsubscribe: (() => Promise<void>) | null = null;

		client
			.l2Book({ coin: apiCoin, nSigFigs, mantissa }, (payload) => {
				if (cancelled) return;
				const r = requestedRef.current;
				if (
					r.coin !== market.coin ||
					r.grouping.nSigFigs !== nSigFigs ||
					r.grouping.mantissa !== mantissa
				) {
					return;
				}
				const snapshot = payload as OrderBookSnapshot;
				if (!payloadMatchesStep(snapshot, expectedStep)) return;
				setDisplayed({ snapshot, coin: market.coin, grouping: r.grouping });
			})
			.then((subscription) => {
				if (cancelled) {
					void subscription.unsubscribe();
					return;
				}
				unsubscribe = () => subscription.unsubscribe();
			})
			.catch(() => {});

		return () => {
			cancelled = true;
			if (unsubscribe) void unsubscribe();
		};
	}, [apiCoin, market.coin, nSigFigs, mantissa, expectedStep]);

	const previousCoinRef = useRef(market.coin);
	if (previousCoinRef.current !== market.coin) {
		previousCoinRef.current = market.coin;
		if (displayed && displayed.coin !== market.coin) {
			setDisplayed(null);
		}
	}

	return {
		...snapshotQuery,
		book: displayed?.snapshot ?? null,
		displayedGrouping: displayed?.grouping ?? null,
	};
}
